import type { IIntegrationCatalogRepository } from '@yawi/domain';
import type {
  CatalogResponse,
  ConfigureProviderRequest,
  IntegrationTestResult,
  ProviderConfigResponse,
  UpdateIntegrationConfigResponse,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

const BASE_URL = 'v1/tenant/integrations/catalog';

export class ApiIntegrationCatalogRepository implements IIntegrationCatalogRepository {
  async getCatalog(tenantId?: string): Promise<CatalogResponse> {
    const client = getApiClient();
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    return client.get<CatalogResponse>(`${BASE_URL}${qs}`);
  }

  async getProviderConfig(provider: string, tenantId?: string): Promise<ProviderConfigResponse> {
    const client = getApiClient();
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    return client.get<ProviderConfigResponse>(`${BASE_URL}/${provider}${qs}`);
  }

  async configureProvider(provider: string, data: ConfigureProviderRequest): Promise<{ integrationId: string; status: string }> {
    const client = getApiClient();
    return client.put<{ integrationId: string; status: string }>(`${BASE_URL}/${provider}`, data);
  }

  async testProvider(provider: string): Promise<IntegrationTestResult> {
    const client = getApiClient();
    return client.post<IntegrationTestResult>(`${BASE_URL}/${provider}/test`);
  }

  async enableIntegration(integrationId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/tenant/integrations/${integrationId}/enable`);
  }

  async disableIntegration(integrationId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/tenant/integrations/${integrationId}/disable`);
  }

  async updateConfig(
    integrationId: string,
    config: Record<string, unknown>,
    tenantId?: string,
  ): Promise<UpdateIntegrationConfigResponse> {
    const client = getApiClient();
    const body: Record<string, unknown> = { config };
    if (tenantId) body.tenantId = tenantId;
    return client.put<UpdateIntegrationConfigResponse>(
      `v1/tenant/integrations/${integrationId}`,
      body,
    );
  }
}
